import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bot,
  FileText,
  Code,
  Terminal,
  FolderTree,
  Settings,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  Sparkles,
} from 'lucide-react'
import Slide, { StaggerContainer, StaggerItem } from '../components/Slide'

interface SlideProps {
  isActive: boolean
}

const sections = [
  {
    id: 'commands',
    icon: Terminal,
    title: 'Build & Test Commands',
    color: 'from-electric-cyan to-blue-500',
    items: [
      'Docker compose up/down',
      'Container build commands',
      'Test execution in containers',
      'Linting & formatting scripts',
    ],
    example: '# Commands\ndocker compose up -d\ndocker compose exec api npm test',
  },
  {
    id: 'architecture',
    icon: FolderTree,
    title: 'Project Architecture',
    color: 'from-electric-purple to-purple-600',
    items: [
      'Directory structure explanation',
      'Read service README for details',
      'Data flow between services',
      'Shared patterns & conventions',
    ],
    example: '# Architecture\nSee api/README.md for backend\nSee frontend/README.md for UI',
  },
  {
    id: 'conventions',
    icon: Code,
    title: 'Coding Conventions',
    color: 'from-aurora-teal to-teal-600',
    items: [
      'Naming standards & patterns',
      'Framework-specific styles',
      'Type strictness rules',
      'Error handling approach',
    ],
    example: '# Style\n- Use camelCase for variables\n- Prefer functional components',
  },
  {
    id: 'workflow',
    icon: Settings,
    title: 'Workflow Rules',
    color: 'from-electric-orange to-orange-600',
    items: [
      'PR & commit conventions',
      'Code review guidelines',
      'Testing requirements',
      'Documentation standards',
    ],
    example: '# Workflow\n- Write tests for new features\n- Run linter before commit',
  },
]

const dosDonts = {
  dos: [
    'Persistent, reusable knowledge',
    'Stable conventions & patterns',
    'Architecture decisions',
    'Common gotchas & warnings',
  ],
  donts: [
    'Single-ticket fixes',
    'Temporary workarounds',
    'Task-specific prompts',
    'Sensitive credentials',
  ],
}

export default function ClaudeMdSlide({ isActive: _isActive }: SlideProps) {
  const [activeSection, setActiveSection] = useState<string | null>(null)
  const [showDos, setShowDos] = useState(true)

  return (
    <Slide>
      <StaggerContainer className="h-full flex flex-col">
        {/* Header */}
        <StaggerItem>
          <div className="mb-4 text-center">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
              <code className="text-gradient">CLAUDE.md</code> Best Practices
            </h2>
            <p className="text-base text-white/60 mb-16">
              Your AI agent's onboarding manual — persistent context for every session
            </p>
          </div>
        </StaggerItem>

        {/* Main content */}
        <StaggerItem className="flex-1">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-full">
            {/* Sections grid */}
            <div className="lg:col-span-2 grid grid-cols-2 gap-3">
              {sections.map((section, index) => {
                const Icon = section.icon
                const isActive = activeSection === section.id

                return (
                  <motion.div
                    key={section.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    onClick={() => setActiveSection(isActive ? null : section.id)}
                    className={`glass rounded-xl p-4 cursor-pointer transition-all duration-300 ${
                      isActive ? 'ring-2 ring-electric-cyan glow-cyan' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-semibold text-white mb-1">
                          {section.title}
                        </h3>
                      </div>
                      <ChevronRight className={`w-4 h-4 text-white/40 transition-transform ${isActive ? 'rotate-90' : ''}`} />
                    </div>

                    <ul className="space-y-1.5">
                      {section.items.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-white/60">
                          <span className="w-1.5 h-1.5 rounded-full bg-electric-cyan/60 mt-1.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>

                    <AnimatePresence>
                      {isActive && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <pre className="mt-3 p-3 rounded-lg bg-space-800/80 text-xs text-electric-cyan font-mono overflow-x-auto">
                            {section.example}
                          </pre>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })}
            </div>

            {/* Dos & Don'ts panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="glass rounded-xl p-4 flex flex-col"
            >
              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => setShowDos(true)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    showDos
                      ? 'bg-electric-cyan/20 text-electric-cyan'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <CheckCircle2 className="w-4 h-4 inline mr-1" />
                  Include
                </button>
                <button
                  onClick={() => setShowDos(false)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    !showDos
                      ? 'bg-red-500/20 text-red-400'
                      : 'text-white/60 hover:text-white'
                  }`}
                >
                  <AlertTriangle className="w-4 h-4 inline mr-1" />
                  Avoid
                </button>
              </div>

              <AnimatePresence mode="wait">
                <motion.div
                  key={showDos ? 'dos' : 'donts'}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex-1"
                >
                  <ul className="space-y-3">
                    {(showDos ? dosDonts.dos : dosDonts.donts).map((item, i) => (
                      <motion.li
                        key={item}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-start gap-3"
                      >
                        {showDos ? (
                          <CheckCircle2 className="w-5 h-5 text-electric-cyan flex-shrink-0" />
                        ) : (
                          <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
                        )}
                        <span className={`text-sm ${showDos ? 'text-white/70' : 'text-white/60'}`}>
                          {item}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              </AnimatePresence>

              {/* Hierarchy tip */}
              <div className="mt-auto pt-4 border-t border-white/10">
                <div className="flex items-start gap-2">
                  <Lightbulb className="w-4 h-4 text-electric-purple flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-white/50">
                    <span className="text-electric-purple font-medium">Hierarchy:</span> Global (~/.claude/) → Project root → Nested folders. Closest file takes precedence.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </StaggerItem>

        {/* Key principles */}
        <StaggerItem>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            className="mt-4 mb-[100px]"
          >
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { icon: FileText, text: 'Agent-facing, not human docs' },
                { icon: Sparkles, text: 'Context without overwhelming' },
                { icon: Bot, text: 'Evolve with project' },
              ].map((principle, i) => (
                <motion.div
                  key={principle.text}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.9 + i * 0.1 }}
                  className="flex items-center gap-2 glass px-4 py-2 rounded-full"
                >
                  <principle.icon className="w-4 h-4 text-electric-cyan" />
                  <span className="text-sm text-white/70">{principle.text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-xs text-white/40 text-center mt-3">
              Sources: JetBrains • Cursor IDE • AGENTS.md Standard • Claude Code Docs
            </p>
          </motion.div>
        </StaggerItem>
      </StaggerContainer>
    </Slide>
  )
}
